import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSyncExternalStore } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { can, type Action, type Resource, type User } from '@mouldtrack/shared';
import { hydrateSession, sessionStore, clearSession } from './session.js';
import { setAuthFailureHandler } from '../api/client.js';
import { startSync, stopSync } from '../sync/engine.js';
import { BootSplash } from '../components/layout/BootSplash.js';

interface AuthContextValue {
  user: User | null;
  signOut: () => Promise<void>;
  /** Permission check bound to the signed-in role. */
  allows: (action: Action, resource: Resource) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  const session = useSyncExternalStore(
    sessionStore.subscribe,
    sessionStore.get,
    () => null,
  );

  useEffect(() => {
    // The API client can only tell the app "your session is unrecoverable"; the
    // app decides what that means (here: drop to the login screen).
    setAuthFailureHandler(() => {
      stopSync();
    });
    void hydrateSession().finally(() => setReady(true));
  }, []);

  // The engine runs exactly as long as somebody is signed in.
  useEffect(() => {
    if (!session) {
      stopSync();
      return;
    }
    void startSync();
    return () => stopSync();
  }, [session]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      signOut: async () => {
        stopSync();
        await clearSession();
      },
      allows: (action, resource) => can(session?.user.role, action, resource),
    }),
    [session],
  );

  // Rendering routes before the session is read would bounce a signed-in user to
  // the login screen for a frame - jarring, and on a slow phone, long enough to
  // start typing into.
  if (!ready) return <BootSplash />;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Convenience for conditional UI: `const canEdit = useCan('update', 'moulds')`. */
export function useCan(action: Action, resource: Resource): boolean {
  return useAuth().allows(action, resource);
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

/** Route-level permission gate. Sends the unauthorised somewhere they can use. */
export function RequirePermission({
  action,
  resource,
  children,
}: {
  action: Action;
  resource: Resource;
  children: ReactNode;
}) {
  const allowed = useCan(action, resource);
  if (!allowed) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Inline guard: renders children only when permitted, with an optional fallback. */
export function Can({
  action,
  resource,
  children,
  fallback = null,
}: {
  action: Action;
  resource: Resource;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return useCan(action, resource) ? <>{children}</> : <>{fallback}</>;
}
